import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function pruneStaleKeys<V>(cache: Map<string, V>, present: Set<string>): void {
    for (const key of cache.keys()) {
        if (!present.has(key)) cache.delete(key);
    }
}

/** Props spread onto a navigable row: a roving tab stop plus arrow handling. */
export interface NarrativeRowNavProps {
    tabIndex: number;
    ref: (el: HTMLElement | null) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
    onFocus: () => void;
    /** Returns real DOM focus to this row — bound to focusRow(key), so it
     *  scrolls the row back into view first if the virtualizer has since
     *  unmounted it. Wired by row components as returnFocusToRow for
     *  useNarrativeRowActions (edit-cancel, edit-save, delete-cancel — see
     *  its doc comment). The row's own `ref` above is the only DOM handle
     *  needed for this; no second local ref required. */
    focusSelf: () => void;
}

export interface NarrativeKeyboardNav {
    /** Props to spread onto a navigable row, addressed by its stable row key. */
    getRowProps: (key: string) => NarrativeRowNavProps;
    /** The row key that currently owns the single tab stop. */
    activeKey: string | null;
    /** Move DOM focus to the row addressed by `key` — scrolling it into view
     *  first if it isn't currently mounted (virtualized out of the window). */
    focusRow: (key: string) => void;
}

interface UseNarrativeKeyboardNavParams {
    /**
     * Row keys in the order they are rendered on screen. ArrowUp moves to the
     * previous key, ArrowDown to the next — so navigation follows what the eye
     * sees in both ASC and DESC without any direction branching here.
     *
     * Keys must be stable and collision-free across the whole list: a pinned
     * entry also appears in the chronological list under the same numeric id, so
     * the pinned copy is namespaced (e.g. `pinned-42`). The divider is a stop
     * too (`pinned-divider`), which is how ArrowDown flows pinned rows → divider
     * → chronological rows as one continuous sequence.
     */
    orderedKeys: string[];
    /** Gate: when false the hook is inert (no tab stop, no key handling). */
    enabled: boolean;
    /**
     * Bring a row into view when it isn't currently mounted, so it can receive
     * focus. The list is virtualized, so arrowing past the rendered window would
     * otherwise target a node that doesn't exist yet.
     */
    scrollRowIntoView?: (key: string) => void;
    preferredKey?: string;
}

/**
 * Keyboard navigation across the narrative list. Implements the roving-tabindex
 * pattern: the list exposes a single tab stop, and ArrowUp/ArrowDown move focus
 * between rows in on-screen order — pinned rows, the expand/collapse divider,
 * and chronological rows form one continuous sequence.
 */
export function useNarrativeKeyboardNav({
    orderedKeys,
    enabled,
    scrollRowIntoView,
    preferredKey,
}: UseNarrativeKeyboardNavParams): NarrativeKeyboardNav {
    const [activeKey, setActiveKey] = useState<string | null>(null);
    const elementsRef = useRef<Map<string, HTMLElement>>(new Map());
    const refCallbacksRef = useRef<Map<string, (el: HTMLElement | null) => void>>(new Map());
    const keyHandlersRef = useRef<
        Map<string, Pick<NarrativeRowNavProps, 'onKeyDown' | 'onFocus' | 'focusSelf'>>
    >(new Map());
    const propsCacheRef = useRef<Map<string, NarrativeRowNavProps>>(new Map());
    // Set when we want to focus a row that isn't mounted yet; the row's ref
    // callback completes the focus once the virtualizer renders it.
    const pendingFocusKeyRef = useRef<string | null>(null);

    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;
    const orderedKeysRef = useRef(orderedKeys);
    orderedKeysRef.current = orderedKeys;
    const scrollRowIntoViewRef = useRef(scrollRowIntoView);
    scrollRowIntoViewRef.current = scrollRowIntoView;

    useEffect(
        function pruneCachesForDepartedKeys() {
            const present = new Set(orderedKeys);
            pruneStaleKeys(elementsRef.current, present);
            pruneStaleKeys(refCallbacksRef.current, present);
            pruneStaleKeys(keyHandlersRef.current, present);
            pruneStaleKeys(propsCacheRef.current, present);
        },
        [orderedKeys]
    );

    // The single tab stop: the active row, or the first row when nothing is
    // active yet. Falls back to the first key if the active row left the set
    // (e.g. its entry was unpinned or scrolled out and unmounted).
    const rovingKey = useMemo(() => {
        if (activeKey !== null && orderedKeys.includes(activeKey)) return activeKey;
        if (preferredKey !== undefined && orderedKeys.includes(preferredKey)) return preferredKey;
        return orderedKeys[0] ?? null;
    }, [activeKey, preferredKey, orderedKeys]);

    const focusRow = useCallback((key: string) => {
        const el = elementsRef.current.get(key);
        if (el != null) {
            el.focus({ preventScroll: true });
            pendingFocusKeyRef.current = null;
            return;
        }
        pendingFocusKeyRef.current = key;
        scrollRowIntoViewRef.current?.(key);
    }, []);

    // Cache ref callbacks per key so a row's ref identity is stable across
    // renders — otherwise React would detach/re-attach the ref every render.
    const getRefCallback = useCallback((key: string) => {
        const existing = refCallbacksRef.current.get(key);
        if (existing !== undefined) return existing;
        const cb = (el: HTMLElement | null): void => {
            if (el != null) {
                elementsRef.current.set(key, el);
                if (pendingFocusKeyRef.current === key) {
                    el.focus({ preventScroll: true });
                    pendingFocusKeyRef.current = null;
                }
            } else {
                elementsRef.current.delete(key);
            }
        };
        refCallbacksRef.current.set(key, cb);
        return cb;
    }, []);

    const getKeyHandlers = useCallback(
        (key: string) => {
            const existing = keyHandlersRef.current.get(key);
            if (existing !== undefined) return existing;
            const handlers: Pick<NarrativeRowNavProps, 'onKeyDown' | 'onFocus' | 'focusSelf'> = {
                onKeyDown: (e) => {
                    if (!enabledRef.current) return;
                    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
                    const keys = orderedKeysRef.current;
                    const index = keys.indexOf(key);
                    if (index === -1) return;
                    const nextKey = e.key === 'ArrowDown' ? keys[index + 1] : keys[index - 1];
                    if (nextKey === undefined) return;
                    e.preventDefault();
                    setActiveKey(nextKey);
                    focusRow(nextKey);
                },
                onFocus: () => {
                    if (enabledRef.current) setActiveKey(key);
                },
                focusSelf: () => focusRow(key),
            };
            keyHandlersRef.current.set(key, handlers);
            return handlers;
        },
        [focusRow]
    );

    const getRowProps = useCallback(
        (key: string): NarrativeRowNavProps => {
            const tabIndex = enabled && key === rovingKey ? 0 : -1;
            const cached = propsCacheRef.current.get(key);
            if (cached !== undefined && cached.tabIndex === tabIndex) return cached;
            const handlers = getKeyHandlers(key);
            const props: NarrativeRowNavProps = {
                tabIndex,
                ref: getRefCallback(key),
                onKeyDown: handlers.onKeyDown,
                onFocus: handlers.onFocus,
                focusSelf: handlers.focusSelf,
            };
            propsCacheRef.current.set(key, props);
            return props;
        },
        [enabled, rovingKey, getRefCallback, getKeyHandlers]
    );

    return useMemo(
        () => ({ getRowProps, activeKey, focusRow }),
        [getRowProps, activeKey, focusRow]
    );
}
